import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  FlatList,
  Keyboard,
} from "react-native";
import { useTranslation } from "react-i18next";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../common/FloatingTextInputComponent";
import {
  validateField,
  getFieldMax,
  getFieldMin,
} from "../../../../helper/fieldHelper";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { useStepAction } from "../hooks/useStepAction";
import { getAdditionalInfoFields } from "../helper/transactionFieldsHelper";
import type { FieldInfo } from "../models/fieldInfo";
import type { EnumerationItem } from "../models/EnumerationItem";

function isEnumField(field: FieldInfo) {
  return (
    (field.dataType || "").toLowerCase() === "enum" &&
    Array.isArray(field.enumerationItem) &&
    field.enumerationItem.length > 0
  );
}

export function AdditionalInfoScreen() {
  const { t } = useTranslation();
  const journeyData = useJourneyStore((state) => state.journeyData);
  const fieldInfo = journeyData.transactionFieldsSendResponse?.fieldInfo ?? [];
  const additionalFields = journeyData.additionalFields;
  const updateJourneyData = useJourneyStore((state) => state.updateJourneyData);
  const { goNext } = useWorkflowNavigation();

  const requiredFields = useMemo(
    () => getAdditionalInfoFields(journeyData),
    [journeyData]
  );

  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [enumDraft, setEnumDraft] = useState<Record<string, string>>({});
  const [focusedEnum, setFocusedEnum] = useState<string | null>(null);
  const enumBlurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      requiredFields.forEach((f) => {
        if (next[f.field] === undefined) {
          next[f.field] = additionalFields[f.field] ?? "";
        }
      });
      return next;
    });
  }, [requiredFields, additionalFields]);

  const getLabel = (field: FieldInfo) => field.fieldLabel || field.field;

  const clearError = (fieldKey: string) => {
    if (errors[fieldKey]) {
      setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
    }
  };

  const updateValue = (fieldKey: string, value: string) => {
    setValues((prev) => ({ ...prev, [fieldKey]: value }));
    clearError(fieldKey);
  };

  const filterEnumItems = (items: EnumerationItem[], query: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.description.toLowerCase().includes(q) ||
        i.value.toLowerCase().includes(q)
    );
  };

  const renderEnumField = (field: FieldInfo) => {
    const items = field.enumerationItem ?? [];
    const value = values[field.field] ?? "";
    const selected = items.find((i) => i.value === value);
    const draft = enumDraft[field.field];
    const inputValue = draft !== undefined ? draft : selected?.description ?? "";
    const filtered = filterEnumItems(items, inputValue);
    const showList = focusedEnum === field.field && filtered.length > 0;

    return (
      <View key={field.field}>
        <FloatingTextInputComponent
          testID={`sendMoney-${field.field}`}
          label={getLabel(field)}
          value={inputValue}
          onChangeText={(text) => {
            setEnumDraft((prev) => ({ ...prev, [field.field]: text }));
            const match = items.find(
              (i) =>
                i.description.toLowerCase() === text.trim().toLowerCase() ||
                i.value.toLowerCase() === text.trim().toLowerCase()
            );
            updateValue(field.field, match?.value ?? "");
          }}
          onFocus={() => {
            if (enumBlurTimerRef.current) {
              clearTimeout(enumBlurTimerRef.current);
            }
            setFocusedEnum(field.field);
          }}
          onBlur={() => {
            enumBlurTimerRef.current = setTimeout(() => {
              setFocusedEnum(null);
            }, 200);
          }}
          errorText={errors[field.field]}
        />
        {showList && (
          <View style={styles.dropdownContainer}>
            <FlatList
              keyboardShouldPersistTaps="handled"
              data={filtered}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.suggestionItem}
                  onPress={() => {
                    updateValue(field.field, item.value);
                    setEnumDraft((prev) => ({
                      ...prev,
                      [field.field]: item.description,
                    }));
                    setFocusedEnum(null);
                    Keyboard.dismiss();
                  }}
                >
                  <Text style={styles.suggestionText}>{item.description}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        )}
        {field.helpTextLong ? (
          <Text style={styles.helpText}>{field.helpTextLong}</Text>
        ) : null}
      </View>
    );
  };

  const renderTextField = (field: FieldInfo, index: number) => {
    const max = getFieldMax(fieldInfo, field.field);
    const min = getFieldMin(fieldInfo, field.field);
    return (
      <View key={field.field}>
        <FloatingTextInputComponent
          testID={`sendMoney-${field.field}`}
          label={getLabel(field)}
          value={values[field.field] ?? ""}
          isAutoFocus={index === 0}
          maxLength={max ? Number(max) : undefined}
          minLength={min ? Number(min) : undefined}
          onChangeText={(val) => updateValue(field.field, val)}
          errorText={errors[field.field]}
        />
        {field.helpTextLong ? (
          <Text style={styles.helpText}>{field.helpTextLong}</Text>
        ) : null}
      </View>
    );
  };

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    requiredFields.forEach((f) => {
      newErrors[f.field] = validateField(
        fieldInfo,
        f.field,
        values[f.field] ?? ""
      );
    });
    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      return;
    }

    updateJourneyData({ additionalFields: values });
    goNext();
  };

  useStepAction(handleNext);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      testID="additional-info-screen"
    >
      <Text style={styles.title}>
        {t("WE_NEED_FEW_MORE_DETAILS") || "We need a few more details"}
      </Text>

      {requiredFields.map((field, index) => {
        if (isEnumField(field)) return renderEnumField(field);
        return renderTextField(field, index);
      })}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 24,
  },
  helpText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: "400",
    color: Colors.subTitleColor,
    lineHeight: 16,
    marginTop: 4,
    marginBottom: 8,
  },
  dropdownContainer: {
    maxHeight: 180,
    borderWidth: 1,
    borderColor: Colors.textInputBackground,
    borderRadius: 8,
    backgroundColor: Colors.white,
    marginTop: 4,
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderColor: Colors.textInputBackground,
  },
  suggestionText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 16,
    color: Colors.titleTextColor,
  },
});
